import { createContext, useContext, useCallback, useState, useMemo, type ReactNode } from 'react'

interface CharacterModalContextType {
  isOpen: boolean
  initialSelectedTemplateId?: string
  openModal: (templateId?: string) => void
  closeModal: () => void
}

const CharacterModalContext = createContext<CharacterModalContextType | null>(null)

interface CharacterModalProviderProps {
  children: ReactNode
}

export function CharacterModalProvider({ children }: CharacterModalProviderProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [initialSelectedTemplateId, setInitialSelectedTemplateId] = useState<string | undefined>()

  const openModal = useCallback((templateId?: string) => {
    setInitialSelectedTemplateId(templateId)
    setIsOpen(true)
  }, [])

  const closeModal = useCallback(() => {
    setIsOpen(false)
    setInitialSelectedTemplateId(undefined)
  }, [])

  const value = useMemo(
    () => ({ isOpen, initialSelectedTemplateId, openModal, closeModal }),
    [isOpen, initialSelectedTemplateId, openModal, closeModal]
  )

  return <CharacterModalContext.Provider value={value}>{children}</CharacterModalContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCharacterModal(): CharacterModalContextType {
  const context = useContext(CharacterModalContext)
  if (!context) {
    throw new Error('useCharacterModal must be used within a CharacterModalProvider')
  }
  return context
}

// eslint-disable-next-line react-refresh/only-export-components
export function useOptionalCharacterModal(): CharacterModalContextType | null {
  return useContext(CharacterModalContext)
}
